import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchProducts, createProduct } from '../api/admin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar } from '@mui/material';

export function ProductsPage() {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    // Form States
    const [newProductName, setNewProductName] = useState('');
    const [newProductVersion, setNewProductVersion] = useState('1.0.0');
    const [newProductDescription, setNewProductDescription] = useState('');

    // Snackbar Toast State
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    const resetCreateForm = () => {
        setNewProductName('');
        setNewProductVersion('1.0.0');
        setNewProductDescription('');
    };

    const handleCloseCreateDialog = () => {
        if (createMutation.isPending) return; // Prevent close & reset during request submission
        setCreateDialogOpen(false);
        resetCreateForm();
    };

    const { data, isLoading } = useQuery({
        queryKey: ['products'],
        queryFn: fetchProducts,
    });

    const createMutation = useMutation({
        mutationFn: createProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['products'] });
            showMessage('Product created successfully');
            setCreateDialogOpen(false);
            resetCreateForm(); // Reset only on success
        },
        onError: (err: any) => {
            showMessage(err.response?.data?.error || 'Failed to create product');
        }
    });

    const handleCreateProduct = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!newProductName.trim()) {
            showMessage('Product name is required');
            return;
        }
        createMutation.mutate({
            name: newProductName.trim(),
            version: newProductVersion.trim(),
            description: newProductDescription.trim(),
            featuresTemplate: {},
        });
    };

    if (isLoading) {
        return <div className="text-center py-8">Loading products...</div>;
    }

    const products = data?.products || [];

    // Client-side search filtering
    const filteredProducts = products.filter((product: any) => {
        const term = searchTerm.toLowerCase();
        return (
            (product.productId || '').toLowerCase().includes(term) ||
            (product.name || '').toLowerCase().includes(term) ||
            (product.description || '').toLowerCase().includes(term) ||
            (product.version || '').toLowerCase().includes(term)
        );
    });

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Products
                </h1>
                <button
                    onClick={() => {
                        resetCreateForm();
                        setCreateDialogOpen(true);
                    }}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                    Create Product
                </button>
            </div>

            {/* Search Filter */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-4 mb-6">
                <div className="max-w-md">
                    <input
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                </div>
            </div>

            {/* Create Product Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                PaperProps={{ className: 'w-[480px] max-w-full !rounded-3xl dark:bg-gray-800 dark:text-white p-2' }}
            >
                <DialogTitle className="!font-bold !text-lg text-gray-900 dark:text-white">Create Product</DialogTitle>
                <form onSubmit={handleCreateProduct}>
                    <DialogContent className="space-y-4 !py-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Product Name
                            </label>
                            <input
                                type="text"
                                value={newProductName}
                                onChange={(e) => setNewProductName(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            {!newProductName.trim() && (
                                <p className="text-[10px] text-red-500 font-semibold mt-1 font-sans">Product name is required</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Version
                            </label>
                            <input
                                type="text"
                                value={newProductVersion}
                                onChange={(e) => setNewProductVersion(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Description
                            </label>
                            <textarea
                                value={newProductDescription}
                                onChange={(e) => setNewProductDescription(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                    </DialogContent>
                    <DialogActions className="!px-6 !pb-4">
                        <Button
                            onClick={handleCloseCreateDialog}
                            disabled={createMutation.isPending}
                            className="!text-gray-500 hover:!bg-slate-100"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={createMutation.isPending || !newProductName.trim()}
                            className="!rounded-lg !px-4"
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Product'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* List */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Product List
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Product ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Version
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredProducts.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400 italic">
                                        No matching products found
                                    </td>
                                </tr>
                            ) : (
                                filteredProducts.map((product: any) => (
                                    <tr key={product.id}>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 font-mono">
                                            {product.productId}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 font-semibold">
                                            {product.name}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 font-mono">
                                            {product.version}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded-full ${product.status === 'active'
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700/40 dark:text-gray-200'
                                                }`}>
                                                {product.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Notification Toast */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </div>
    );
}